<script setup lang="ts">
import {
  Menu as HeadlessMenu,
  MenuButton,
  MenuItem as HeadlessMenuItem,
  MenuItems,
} from '@headlessui/vue'
import { ref } from 'vue'

import type { MenuItemOption, MenuProps, MenuTriggerSize } from './types'

import { useFloating } from '@/shared/ui/floating'
import { Icon } from '@/shared/ui/icon'
import { FloatingLayer, OverlayPortal } from '@/shared/ui/overlay'

// One map per trigger, Button-style: each branch owns colour, border-width and padding outright,
// because two utilities for the same property are emitted at equal specificity and would be
// decided by stylesheet order rather than by the class list.
const TRIGGER_BASE_CLASS =
  'ui-menu__button inline-flex cursor-pointer items-center justify-center rounded-sm outline-none focus-visible:shadow-[0_0_0_2px_var(--ui-color-ring)]'

const TRIGGER_VARIANT_CLASS: Record<NonNullable<MenuProps['trigger']>, string> = {
  default: 'min-h-8 border border-border bg-surface px-sm text-fg focus-visible:border-ring',
  icon: 'ui-menu__button--icon border-0 bg-transparent p-0 text-muted hover:bg-surface-sunken hover:text-primary',
}

const TRIGGER_ICON_SIZE_CLASS: Record<MenuTriggerSize, string> = {
  xs: 'size-6',
  sm: 'size-7',
  md: 'size-8',
  lg: 'size-10',
}

const props = withDefaults(defineProps<MenuProps>(), {
  placement: 'bottom',
  trigger: 'default',
  size: 'md',
})

// An icon trigger has no text node, so `label` is its ONLY accessible name. The type already makes
// it required; this catches the empty string that types cannot. Ungated by environment on purpose:
// a raw env read here would take ownership away from `shared/config/env.ts` (contract 01 §5), and
// a nameless icon button is a defect worth surfacing wherever it ships.
if (props.trigger === 'icon' && props.label.trim() === '') {
  console.warn('[ui/Menu] trigger="icon" needs a non-empty `label`: it is the button\'s only name.')
}

const emit = defineEmits<{ select: [item: MenuItemOption] }>()
const reference = ref<HTMLElement | null>(null)
const floating = ref<HTMLElement | null>(null)
const { floatingStyles } = useFloating(reference, floating, {
  placement: props.placement,
  offset: 4,
  strategy: 'fixed',
})
</script>

<template>
  <HeadlessMenu v-slot="{ open }" as="div" class="ui-menu inline-flex font-sans">
    <span ref="reference" class="ui-menu__trigger inline-flex">
      <MenuButton
        :class="[
          TRIGGER_BASE_CLASS,
          TRIGGER_VARIANT_CLASS[trigger],
          trigger === 'icon' ? TRIGGER_ICON_SIZE_CLASS[size] : '',
        ]"
        :aria-label="label"
      >
        <slot name="trigger" :open="open">{{ label }}</slot>
      </MenuButton>
    </span>

    <OverlayPortal>
      <FloatingLayer
        v-if="open"
        v-model:element="floating"
        kind="menu"
        class="ui-menu__floating min-w-40"
        :style="floatingStyles"
      >
        <MenuItems
          class="ui-menu__items m-0 flex list-none flex-col gap-0.5 overflow-y-auto rounded-md border border-border bg-surface p-2xs shadow-pop outline-none"
          :style="maxHeight ? { maxHeight } : undefined"
        >
          <HeadlessMenuItem
            v-for="item in items"
            :key="item.id"
            v-slot="{ active, disabled }"
            :disabled="item.disabled === true"
          >
            <button
              type="button"
              class="ui-menu__item flex w-full cursor-pointer items-center gap-sm rounded-sm border-0 bg-transparent px-sm py-xs text-start text-sm text-fg"
              :class="{
                'ui-menu__item--active bg-surface-sunken': active,
                'ui-menu__item--disabled cursor-not-allowed opacity-50': disabled,
                'ui-menu__item--danger text-danger': item.danger,
              }"
              @click="emit('select', item)"
            >
              <slot name="item" :item="item">
                <Icon
                  v-if="item.icon"
                  :name="item.icon"
                  :size="18"
                  weight="light"
                  aria-hidden="true"
                />
                <span>{{ item.label }}</span>
              </slot>
            </button>
          </HeadlessMenuItem>
        </MenuItems>
      </FloatingLayer>
    </OverlayPortal>
  </HeadlessMenu>
</template>
